import { createContext, useContext, useState, useCallback, useRef, ReactNode } from "react";
import { Box } from "@mui/material";

type Politeness = "polite" | "assertive";

interface LiveAnnouncerContextType {
  /**
   * Announce a message to screen readers.
   * @param message - The message to announce
   * @param politeness - 'polite' (default) waits for idle, 'assertive' interrupts
   */
  announce: (message: string, politeness?: Politeness) => void;
}

const LiveAnnouncerContext = createContext<LiveAnnouncerContextType | null>(null);

interface LiveAnnouncerProviderProps {
  children: ReactNode;
}

/**
 * Provider for screen reader announcements.
 * Wrap your app with this provider to enable live region announcements.
 *
 * @example
 * // In App.tsx
 * <LiveAnnouncerProvider>
 *   <App />
 * </LiveAnnouncerProvider>
 *
 * // In components
 * const { announce } = useLiveAnnouncer();
 * announce('Item saved successfully');
 */
export function LiveAnnouncerProvider({ children }: LiveAnnouncerProviderProps) {
  const [politeMessage, setPoliteMessage] = useState("");
  const [assertiveMessage, setAssertiveMessage] = useState("");

  // Debounce timer refs to prevent rapid announcements
  const politeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const assertiveTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const announce = useCallback((message: string, politeness: Politeness = "polite") => {
    // Clear the message first to ensure re-announcement of same message
    if (politeness === "assertive") {
      if (assertiveTimerRef.current) {
        clearTimeout(assertiveTimerRef.current);
      }
      setAssertiveMessage("");
      assertiveTimerRef.current = setTimeout(() => {
        setAssertiveMessage(message);
      }, 100);
    } else {
      if (politeTimerRef.current) {
        clearTimeout(politeTimerRef.current);
      }
      setPoliteMessage("");
      politeTimerRef.current = setTimeout(() => {
        setPoliteMessage(message);
      }, 100);
    }
  }, []);

  return (
    <LiveAnnouncerContext.Provider value={{ announce }}>
      {children}
      {/* Polite live region - waits for idle */}
      <Box
        component="div"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "1px",
          height: "1px",
          padding: 0,
          margin: "-1px",
          overflow: "hidden",
          clipPath: "inset(50%)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {politeMessage}
      </Box>
      {/* Assertive live region - interrupts immediately */}
      <Box
        component="div"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "1px",
          height: "1px",
          padding: 0,
          margin: "-1px",
          overflow: "hidden",
          clipPath: "inset(50%)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {assertiveMessage}
      </Box>
    </LiveAnnouncerContext.Provider>
  );
}

/**
 * Hook to access the live announcer.
 * Must be used within a LiveAnnouncerProvider.
 *
 * @example
 * const { announce } = useLiveAnnouncer();
 *
 * // Polite announcement (default) - for status updates
 * announce('Changes saved');
 *
 * // Assertive announcement - for errors or urgent info
 * announce('Error: Failed to save', 'assertive');
 */
export function useLiveAnnouncer(): LiveAnnouncerContextType {
  const context = useContext(LiveAnnouncerContext);
  if (!context) {
    throw new Error("useLiveAnnouncer must be used within a LiveAnnouncerProvider");
  }
  return context;
}

export default LiveAnnouncerContext;
